"use client";

import { createContext, useContext } from "react";
import type { Dict } from "@/lib/i18n";

/**
 * Runtime-translations provider (B15b). The server layout computes the merged
 * Dict once — `overlayDict(dict(lang), await loadTranslations(lang))` — and
 * hands it down here so every client component reads per-portal copy overrides
 * via `useDict()` instead of the baked `dict(lang)`. With no overrides
 * configured the merged Dict is identical to the baked one, so this is a no-op
 * until a portal customizes wording (mirrors how `portal.theme` overlays colour).
 *
 * Context is a Client Component, but it's mounted in a Server Component layout
 * wrapping `{children}` — server components in between render fine; only the
 * Client Components that call `useDict()` consume it.
 */
const DictContext = createContext<Dict | null>(null);

export function DictProvider({ value, children }: { value: Dict; children: React.ReactNode }) {
  return <DictContext.Provider value={value}>{children}</DictContext.Provider>;
}

/** Client-side accessor for the (possibly overlaid) Dict. Must be called under
 *  a `<DictProvider>` — the root `[lang]` layout mounts one for the whole tree. */
export function useDict(): Dict {
  const d = useContext(DictContext);
  if (d === null) {
    throw new Error("useDict() must be used within a <DictProvider> (mounted in [lang]/layout).");
  }
  return d;
}
